import { create } from 'zustand';
import apiClient from '../services/axiosInterceptor';

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface SessionDetails {
  id: string;
  school_id: string;
  session: string;
  created_by: string;
  start_date?: string;
  end_date?: string;
  start_month?: number;
  end_month?: number;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

export interface SchoolDetails {
  school_id: string;
  school_id_code: string;
  name: string;
  state: string;
  district: string;
  pincode: string;
  school_type: string;
  status: string;
  created_at: string;
  has_admin: boolean;
  current_session: SessionDetails | null;
  base_session: SessionDetails | null;
}

interface SchoolDetailsState {
  schoolDetails: SchoolDetails | null;
  loading: boolean;
  error: string | null;
  
  fetchSchoolDetails: (schoolId: string) => Promise<void>;
  clearSchoolDetails: () => void;
}

// ── Store Creation ────────────────────────────────────────────────────────────

export const useSchoolDetailsStore = create<SchoolDetailsState>((set) => ({
  schoolDetails: null,
  loading: false,
  error: null,

  fetchSchoolDetails: async (schoolId: string) => {
    if (!schoolId) {
      set({ error: 'School ID is required', loading: false });
      return;
    }

    set({ loading: true, error: null });

    try {
      // Call the new GET /api/schools/:id/details endpoint
      const response = await apiClient.get(`/api/schools/${schoolId}/details`);
      
      if (response.data.status === 'success' && response.data.data) {
        set({
          schoolDetails: response.data.data,
          loading: false,
          error: null,
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch school details');
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to load school details';

      set({
        schoolDetails: null,
        loading: false,
        error: errorMessage,
      });
    }
  },

  clearSchoolDetails: () => {
    set({ schoolDetails: null, error: null });
  },
}));